// Painel do regulador: grandezas em tempo real, alarmes ativos e os bits de status.

import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, ChartLine, CircleOff, Clock, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { Bars } from "../components/charts/Bars";
import { NominalNotice, pctOf, readingMap, TagGauge } from "../components/charts/Readings";
import { Badge, Card, EmptyState, PageHeader, Spinner, Table, cx } from "../components/ui";
import { api, type Live, type LiveAnalog, type LiveWord, type Nominal, type RegulatorDetail, type UnitPrefs } from "../lib/api";
import { BAR_GROUPS, CHARTED_TAGS, GAUGES, REGULATOR_GAUGES } from "../lib/gauges";
import { useMe } from "../lib/session";
import { displayFor, formatValue } from "../lib/units";
import { useAnalysisQuota } from "./analysis";

export function RegulatorPage() {
  const regulatorId = Number(useParams().id);
  const { data: me } = useMe();
  const aiEnabled = !!useAnalysisQuota().data?.enabled;
  const reg = useQuery({
    queryKey: ["regulator", regulatorId],
    queryFn: () => api.get<RegulatorDetail>(`/api/regulators/${regulatorId}`),
  });
  // O tempo real invalida esta consulta; o intervalo é só a rede de segurança.
  const live = useQuery({
    queryKey: ["live", regulatorId],
    queryFn: () => api.get<Live>(`/api/regulators/${regulatorId}/live`),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  if (reg.isLoading) return <Spinner />;
  if (reg.error || !reg.data) return <EmptyState title="Regulador não encontrado" />;
  const r = reg.data;

  return (
    <>
      <PageHeader
        title={r.name}
        description={`${r.group.name} › ${r.site.name} › ${r.unit.name}`}
        actions={
          <div className="flex gap-2">
            <Link
              to={`/graficos?reg=${r.id}`}
              className="inline-flex h-10 items-center gap-2 rounded-md border border-line bg-surface px-4 text-sm font-medium hover:bg-surface-2"
            >
              <ChartLine className="size-4" aria-hidden /> Gráficos
            </Link>
            {aiEnabled && (
              <Link
                to={`/analises?reg=${r.id}`}
                className="inline-flex h-10 items-center gap-2 rounded-md border border-line bg-surface px-4 text-sm font-medium hover:bg-surface-2"
              >
                <Sparkles className="size-4" aria-hidden /> Perguntar à IA
              </Link>
            )}
          </div>
        }
      />

      <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        {r.online ? (
          <Badge tone="ok">
            <Activity className="size-3" aria-hidden /> online
          </Badge>
        ) : (
          <Badge tone="warn">
            <CircleOff className="size-3" aria-hidden /> {r.online === false ? "sem link com o regulador" : "sem dados"}
          </Badge>
        )}
        <span className="flex items-center gap-1.5 text-muted">
          <Clock className="size-3.5" aria-hidden />
          último dado {r.lastSeen ? new Date(r.lastSeen).toLocaleString("pt-BR") : "—"}
        </span>
        <span className="text-muted">{r.model === "vtv" ? "GRTD-2000 VTV" : "GRTD-2000"}</span>
        {r.meta && <span className="text-muted">gateway v{r.meta.gatewayVersion}</span>}
        {!r.online && r.linkDetail && <span className="text-warn">{r.linkDetail}</span>}
      </div>

      {live.isLoading ? (
        <Spinner />
      ) : !live.data ? null : (
        <div className="space-y-8">
          <AlarmsCard alarms={live.data.activeAlarms} />
          <AnalogSection analog={live.data.analog} nominal={r.nominal} prefs={me?.unitPrefs} online={!!r.online} />
          <StatusSection words={live.data.status} />
        </div>
      )}
    </>
  );
}

function AlarmsCard({ alarms }: { alarms: Live["activeAlarms"] }) {
  if (!alarms.length) {
    return (
      <Card className="flex items-center gap-2 p-4 text-sm text-muted">
        <span className="size-2.5 rounded-full bg-ok" aria-hidden /> Nenhum alarme ativo.
      </Card>
    );
  }
  return (
    <Card className="border-danger/40">
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        <AlertTriangle className="size-4 text-danger" aria-hidden />
        <h2 className="font-semibold">
          {alarms.length} alarme{alarms.length > 1 ? "s" : ""} ativo{alarms.length > 1 ? "s" : ""}
        </h2>
      </div>
      <ul className="divide-y divide-line">
        {alarms.map((a) => (
          <li key={`${a.word}.${a.bit}`} className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-2.5 text-sm">
            <span>
              <span className="font-medium">{a.description}</span>{" "}
              <code className="font-mono text-xs text-muted">{a.tag}</code>
            </span>
            <span className="text-xs text-muted">desde {new Date(a.since).toLocaleString("pt-BR")}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function AnalogSection({ analog, nominal, prefs, online }: { analog: LiveAnalog[]; nominal: Nominal; prefs: UnitPrefs | undefined; online: boolean }) {
  const readings = useMemo(() => readingMap(analog), [analog]);
  const stale = !online;

  // Grupos de barras: cada um numa unidade só, com o nominal tracejado.
  const bars = useMemo(
    () =>
      BAR_GROUPS.map((g) => {
        const present = g.tags.filter(([t]) => readings.has(t));
        if (!present.length) return null;
        const first = readings.get(present[0]![0])!;
        const d = displayFor(first.base, first.unit, prefs);
        const nom = g.nominal ? nominal[g.nominal]?.value : undefined;
        const items = present.map(([t, label]) => {
          const a = readings.get(t)!;
          return { label, value: a.value / d.factor, text: `${formatValue(a.value, d)} ${d.unit}` };
        });
        const top = Math.max(...items.map((i) => i.value ?? 0));
        // Sem nominal (as posições já em %): 0 a 100 %.
        const max = nom ? niceMax(Math.max((1.3 * nom) / d.factor, top * 1.1)) : Math.max(100, niceMax(top * 1.1));
        return { title: g.title, items, max, unit: d.unit, mark: nom ? nom / d.factor : undefined };
      }).filter((g) => g !== null),
    [readings, nominal, prefs],
  );
  const others = analog.filter((a) => !CHARTED_TAGS.has(a.tag));

  if (!analog.length) return <EmptyState title="Nenhuma grandeza recebida ainda" />;
  const ts = analog[0]?.ts;

  return (
    <section className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="font-semibold">Grandezas</h2>
        {ts && <span className="text-xs text-muted">leitura de {new Date(ts).toLocaleTimeString("pt-BR")}</span>}
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-4">
          {REGULATOR_GAUGES.map((tag) => (
            <TagGauge key={tag} tag={tag} readings={readings} nominal={nominal} prefs={prefs} stale={stale} />
          ))}
        </div>
        <div className="mt-3">
          <NominalNotice nominal={nominal} />
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {bars.map((g) => (
          <Card key={g.title} className="p-4">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
              {g.title}
              {g.mark !== undefined && <span className="font-normal normal-case tracking-normal"> · tracejado = nominal</span>}
            </h3>
            <Bars title={g.title} items={g.items} max={g.max} mark={g.mark} unit={g.unit} stale={stale} />
          </Card>
        ))}
      </div>

      {others.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {others.map((a) => {
            const d = displayFor(a.base, a.unit, prefs);
            return (
              <Card key={a.tag} className="p-3.5">
                <p className="truncate text-xs text-muted" title={a.description}>
                  {a.description}
                </p>
                <p className="mt-1 text-2xl font-semibold">
                  {formatValue(a.value, d)}
                  {d.unit && <span className="ml-1 text-sm font-normal text-muted">{d.unit}</span>}
                </p>
              </Card>
            );
          })}
        </div>
      )}

      {/* A mesma leitura em tabela: o equivalente acessível dos gráficos. */}
      <details className="rounded-lg border border-line bg-surface">
        <summary className="cursor-pointer px-4 py-2.5 text-sm text-muted select-none">Todas as grandezas em tabela</summary>
        <div className="border-t border-line">
          <Table>
            <thead>
              <tr>
                <th>Grandeza</th>
                <th className="text-right">Valor</th>
                <th>Unidade</th>
                <th className="text-right">% do nominal</th>
              </tr>
            </thead>
            <tbody>
              {analog.map((a) => {
                const d = displayFor(a.base, a.unit, prefs);
                const key = GAUGES[a.tag]?.nominal ?? BAR_GROUPS.find((g) => g.tags.some(([t]) => t === a.tag))?.nominal ?? null;
                const pct = key ? pctOf(a.value, nominal[key]?.value) : null;
                return (
                  <tr key={a.tag}>
                    <td>
                      {a.description} <code className="font-mono text-xs text-muted">{a.tag}</code>
                    </td>
                    <td className="text-right tabular-nums">{formatValue(a.value, d)}</td>
                    <td className="text-muted">{d.unit}</td>
                    <td className="text-right tabular-nums text-muted">{pct === null ? "—" : `${Math.round(pct)}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </details>
    </section>
  );
}

/** Topo de escala "redondo": 1, 2, 2,5 ou 5 × 10^n acima do valor. */
function niceMax(v: number): number {
  if (!(v > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

function StatusSection({ words }: { words: LiveWord[] }) {
  const [onlyOn, setOnlyOn] = useState(false);
  if (!words.length) return null;
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-semibold">Estados digitais</h2>
        <label className="flex items-center gap-2 text-sm text-muted">
          <input type="checkbox" checked={onlyOn} onChange={(e) => setOnlyOn(e.target.checked)} className="accent-[var(--brand)]" />
          só os ligados
        </label>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {words.map((w) => {
          const bits = onlyOn ? w.bits.filter((b) => b.value) : w.bits;
          return (
            <Card key={w.addr} className="p-4">
              <p className="text-sm font-medium">{w.description}</p>
              <p className="mb-2 text-xs text-muted">
                <code className="font-mono">{w.tag}</code> · reg {w.addr}
              </p>
              {bits.length === 0 ? (
                <p className="text-xs text-muted">nenhum ligado</p>
              ) : (
                <ul className="space-y-1">
                  {bits.map((b) => (
                    <li key={b.tag} className="flex items-start gap-2 text-sm">
                      {/* Padrão de LED do app: cinza = 0, verde = 1. O estado também vai em texto. */}
                      <span
                        className={cx("mt-1 size-2.5 shrink-0 rounded-full", b.value ? "bg-[#2ECC71]" : "bg-[#5A5A5A]")}
                        aria-hidden
                      />
                      <span className={b.value ? "text-fg" : "text-muted"}>
                        {b.description}
                        <span className="sr-only">: {b.value ? "ligado" : "desligado"}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          );
        })}
      </div>
    </section>
  );
}
